/**
 * Logistics Hub — capability-first taxonomy for `/app/logistica`.
 *
 * SoT: `LOGISTICS_CATALOG` in `@structura/core/shipping` (+ native overlays below).
 * CONDITIONAL: no `sm_logistics_capability_providers` table/view in this pass —
 * persist a niche matrix in DB only when catalog TS is no longer enough
 * (per-tenant overrides, regional niches, or ops-editable coverage).
 */
import {
  LOGISTICS_CATALOG,
  LOGISTICS_SERVICE_DESCRIPTIONS,
  LOGISTICS_SERVICE_LABELS,
  type LogisticsServiceType,
} from "@structura/core/shipping";

export { LOGISTICS_SERVICE_DESCRIPTIONS };
import {
  isLogisticsProviderId,
  type LogisticsProviderId,
} from "@/lib/logistics-providers";

export type { LogisticsServiceType };

/** Ordered service types for stable hub grids. */
export const HUB_SERVICE_TYPE_ORDER: LogisticsServiceType[] = [
  "pickup",
  "immediate",
  "same_day",
  "next_day",
  "scheduled",
  "national_parcel",
  "branch_pickup",
  "coordinate",
];

/**
 * Native Structura providers (not in LOGISTICS_CATALOG) → capabilities.
 * - entrega: retiro en local + coordinar por WA/tel
 * - zones / radius: flota propia (cobertura local ≈ envío inmediato)
 */
export const NATIVE_CAPABILITY_COVERAGE: Record<
  "entrega" | "zones" | "radius",
  LogisticsServiceType[]
> = {
  entrega: ["pickup", "coordinate"],
  zones: ["immediate"],
  radius: ["immediate"],
};

export type HubCapability = {
  id: LogisticsServiceType;
  label: string;
  description: string;
  /** Logistics hub provider ids that cover this capability. */
  providerIds: LogisticsProviderId[];
};

export type HubProviderRowLike = {
  id: LogisticsProviderId;
  entitled?: boolean;
  configurable?: boolean;
  lifecycle?: string;
  kind?: "native" | "carrier";
  tenant?: {
    enabled?: boolean;
    configured?: boolean;
  } | null;
};

const SERVICE_TYPE_SET = new Set<string>(HUB_SERVICE_TYPE_ORDER);

export function isLogisticsServiceType(id: string): id is LogisticsServiceType {
  return SERVICE_TYPE_SET.has(id);
}

function catalogIdToHubProvider(id: string): LogisticsProviderId | null {
  if (isLogisticsProviderId(id)) return id;
  return null;
}

/** Unique service types with label + provider ids covering them. */
export function listHubCapabilities(): HubCapability[] {
  const byType = new Map<LogisticsServiceType, Set<LogisticsProviderId>>();

  for (const st of HUB_SERVICE_TYPE_ORDER) {
    byType.set(st, new Set());
  }

  for (const entry of LOGISTICS_CATALOG) {
    const providerId = catalogIdToHubProvider(String(entry.id));
    if (!providerId) continue;
    for (const st of entry.serviceTypes) {
      byType.get(st)?.add(providerId);
    }
  }

  for (const [nativeId, services] of Object.entries(NATIVE_CAPABILITY_COVERAGE) as Array<
    [keyof typeof NATIVE_CAPABILITY_COVERAGE, LogisticsServiceType[]]
  >) {
    for (const st of services) {
      byType.get(st)?.add(nativeId);
    }
  }

  return HUB_SERVICE_TYPE_ORDER.map((id) => ({
    id,
    label: LOGISTICS_SERVICE_LABELS[id],
    description: LOGISTICS_SERVICE_DESCRIPTIONS[id],
    providerIds: [...(byType.get(id) ?? [])],
  })).filter((c) => c.providerIds.length > 0);
}

/** Filter provider rows to those covering a capability. */
export function providersForCapability<T extends HubProviderRowLike>(
  capability: LogisticsServiceType,
  providerRows: T[],
): T[] {
  const cap = listHubCapabilities().find((c) => c.id === capability);
  if (!cap) return [];
  const allowed = new Set(cap.providerIds);
  return providerRows.filter((p) => allowed.has(p.id) && p.lifecycle !== "deprecated");
}

export function isProviderActiveForHub(p: HubProviderRowLike): boolean {
  if (!p.entitled) return false;
  if (p.lifecycle === "upcoming" || p.configurable === false) return false;
  if (p.tenant?.enabled && (p.kind === "native" || p.tenant.configured || p.id === "entrega")) {
    return true;
  }
  return false;
}

/** Counts for capability cards (entitled vs active among covering providers). */
export function capabilityProviderCounts(
  capability: LogisticsServiceType,
  providerRows: HubProviderRowLike[],
): { entitled: number; active: number; total: number } {
  const rows = providersForCapability(capability, providerRows);
  const entitled = rows.filter((p) => p.entitled && p.configurable !== false).length;
  const active = rows.filter((p) => isProviderActiveForHub(p)).length;
  return { entitled, active, total: rows.length };
}

/** First capability that includes a provider (legacy deep-links with only `?provider=`). */
export function primaryCapabilityForProvider(
  providerId: LogisticsProviderId,
): LogisticsServiceType | undefined {
  return listHubCapabilities().find((c) => c.providerIds.includes(providerId))?.id;
}


/** Accesos directos a flota propia — visibles en el hub nivel 1. */
export const OWN_LOGISTICS_HUB_CARDS: Array<{
  id: "entrega" | "zones" | "radius";
  title: string;
  description: string;
  /** Si se setea, abre zonas con tipo de match prefijado. */
  draw?: "polygon";
  capabilityHint: LogisticsServiceType;
}> = [
  {
    id: "entrega",
    title: "Métodos de entrega",
    description: "Retiro en local, domicilio o ambos, y coordinación por WhatsApp.",
    capabilityHint: "pickup",
  },
  {
    id: "zones",
    title: "Zonas CP / barrio / provincia",
    description: "Precio y cobertura por código postal, localidad o provincia.",
    capabilityHint: "immediate",
  },
  {
    id: "radius",
    title: "Radio en kilómetros",
    description: "Cobertura por distancia real desde la sucursal mapeada.",
    capabilityHint: "immediate",
  },
  {
    id: "zones",
    title: "Área en el mapa (polígono)",
    description: "Dibujá a mano la zona de delivery sobre el mapa. Ideal para barrios o manzanas.",
    draw: "polygon",
    capabilityHint: "immediate",
  },
];
